/**
 * GroupedTaskList — renders tasks grouped by parent container.
 *
 * Used in Today, Anyday, and Someday views to match v1's layout:
 *   1. Root tasks (no project) — shown first, no header
 *   2. Project/list groups — each with a ContainerGroupHeader, sorted by sidebar order
 *
 * Empty groups (0 tasks after filtering) are automatically hidden.
 *
 * **DnD**: Uses the @dnd-kit multi-container pattern:
 *   - Each group has its own SortableContext (correct per-group animation).
 *   - Each group is wrapped in DroppableGroup (drop target for cross-group).
 *   - onDragOver: when the pointer enters a different group, the active item
 *     is moved to the target group in local state → target SortableContext
 *     picks it up and shows the placeholder correctly.
 *   - onDragEnd: persists final position (same-group reorder or cross-group move).
 */

import { useMemo, useCallback, useState, useRef, useEffect, memo } from "react"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import type { DragOverEvent, DragEndEvent } from "@dnd-kit/core"
import { useVirtualizer } from "@tanstack/react-virtual"
import type { ViewItem } from "../hooks/useViewData"
import { TaskItem } from "@/features/tasks/components/TaskItem"
import { SortableTaskRow } from "@/features/dnd/SortableTaskRow"
import { DndListProvider } from "@/features/dnd/DndListProvider"
import { DroppableGroup } from "@/features/dnd/DroppableGroup"
import { useTaskActions } from "@/features/tasks/hooks/useTaskActions"
import { useActiveTask } from "@/features/task-detail/hooks/useActiveTask"
import { useMainScroll } from "@/app/providers/MainScrollProvider"
import { ContainerGroupHeader } from "./ContainerGroupHeader"
import {
  SortableGroupHeader,
  HEADER_ID_PREFIX,
  headerSortableId,
} from "@/features/dnd/SortableGroupHeader"
import { ObjectType, ObjectStatus } from "@/db/schema"
import { useAncestorTagStore } from "@/features/tags/ancestorTagStore"
import type { ContainerProgress } from "@/features/tags/ancestorTagStore"
import { useViewContext } from "@/app/stores/viewContext"
import {
  reorderIndex,
  nextIndex,
  distributeIndexes,
} from "@/shared/utils/list/indexUtils"
import {
  reorderTaskDual,
  computeGhostIndex,
  crossGroupReorder,
  bulkReorderDual,
} from "@/db/operations"
import type { BulkReorderItem } from "@/db/operations"
import { hapticMedium } from "@/shared/utils/platform/haptics"
import { useTagFilterStore } from "@/features/tags/tagFilterStore"
import { useViewFilterStore } from "../stores/viewFilterStore"
import { ShowMoreButton } from "./ShowMoreButton"
import { computeGroupPositions } from "../utils/groupPositions"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TaskGroup {
  /** "root" for ungrouped tasks, otherwise the containerId */
  containerId: string
  /** Container title (empty for root) */
  title: string
  /** Container type */
  type: ObjectType
  /** Container progress data */
  progress: ContainerProgress
  /** Sidebar sort order (Infinity for root = top) */
  sortIndex: number
  /** Tasks in this group */
  items: ViewItem[]
}

const ROOT_GROUP_ID = "__root__"

const DEFAULT_PROGRESS: ContainerProgress = {
  completed: 0,
  total: 0,
  unfinished: 0,
  status: ObjectStatus.Unfinished,
}

// ---------------------------------------------------------------------------
// Virtualization constants
// ---------------------------------------------------------------------------

/** Virtualize task rows within a group above this threshold */
const VIRTUALIZE_GROUP_THRESHOLD = 20
/** Standard task row height (min-h-11 = 44px) */
const ROW_HEIGHT = 44
/** Section row height: ROW_HEIGHT + 30px margin-top + 10px margin-bottom */
const SECTION_ROW_HEIGHT = 84
/** Number of offscreen rows to keep rendered */
const VIRTUAL_OVERSCAN = 15

// ---------------------------------------------------------------------------
// Grouping logic (pure function)
// ---------------------------------------------------------------------------

/**
 * Group items by their parent container, maintaining item order within groups.
 * Root tasks (parentPath = "root", "root/#inbox", "root/#anyday") go into
 * a synthetic root group rendered first (no header).
 */
function groupItems(
  items: ViewItem[],
  containerSortIndex: Record<string, number>,
  containerTitles: Record<string, string>,
  containerTypes: Record<string, ObjectType>,
  containerProgress: Record<string, ContainerProgress>,
): TaskGroup[] {
  const groupMap = new Map<string, TaskGroup>()

  for (const item of items) {
    // Projects/Lists always go in root group (they ARE containers)
    const isContainer =
      item.task.type === ObjectType.Project ||
      item.task.type === ObjectType.List

    let containerId: string
    if (isContainer) {
      containerId = ROOT_GROUP_ID
    } else {
      const parentPath = item.task.parentPath
      const segments = parentPath.split("/")
      // parentPath = "root/{containerId}" → containerId
      // parentPath = "root" or "root/#xyz" → root group
      containerId =
        segments.length === 2 && !segments[1]!.startsWith("#")
          ? segments[1]!
          : ROOT_GROUP_ID
    }

    let group = groupMap.get(containerId)
    if (!group) {
      const isRoot = containerId === ROOT_GROUP_ID
      group = {
        containerId,
        title: isRoot ? "" : (containerTitles[containerId] ?? ""),
        type: isRoot
          ? ObjectType.Task
          : (containerTypes[containerId] ?? ObjectType.Project),
        progress: isRoot
          ? DEFAULT_PROGRESS
          : (containerProgress[containerId] ?? DEFAULT_PROGRESS),
        sortIndex: isRoot
          ? -Infinity // root group always first
          : (containerSortIndex[containerId] ?? Infinity),
        items: [],
      }
      groupMap.set(containerId, group)
    }
    group.items.push(item)
  }

  // Sort groups: root first, then by sidebar sort index
  return Array.from(groupMap.values()).sort((a, b) => a.sortIndex - b.sortIndex)
}

// ---------------------------------------------------------------------------
// DnD header-boundary resolution helpers
// ---------------------------------------------------------------------------

/** Vertical midpoint of the dragged item's current translated position. */
function getTranslatedMidY(active: DragOverEvent["active"]): number | null {
  try {
    const t = active.rect?.current?.translated
    return t ? t.top + t.height / 2 : null
  } catch {
    return null
  }
}

/**
 * Resolve effective group when hovering near a group header.
 *
 * Headers sit at boundaries between groups. When the active item's center
 * is ABOVE the header center, the effective group is the one above the
 * header — not the header's own group. This handles:
 *   1. Own-header barrier — redirects upward (header escape)
 *   2. Oscillation prevention — after escaping, prevents snap-back when
 *      the header is still the nearest collision target
 */
function resolveHeaderOverGroup(
  headerGroupId: string,
  activeMidY: number | null,
  headerRect: { top: number; height: number },
  groups: TaskGroup[],
): { groupId: string; escaped: boolean } {
  if (activeMidY != null) {
    const headerMid = headerRect.top + headerRect.height / 2
    if (activeMidY < headerMid) {
      const idx = groups.findIndex((g) => g.containerId === headerGroupId)
      if (idx > 0) {
        return { groupId: groups[idx - 1]!.containerId, escaped: true }
      }
    }
  }
  return { groupId: headerGroupId, escaped: false }
}

// ---------------------------------------------------------------------------
// DnD direction & index helpers
// ---------------------------------------------------------------------------

/** Other index dimension — the "ghost" field for dual-index writes. */
function otherIndexField(
  field: "index" | "startAtIndex",
): "index" | "startAtIndex" {
  return field === "startAtIndex" ? "index" : "startAtIndex"
}

/**
 * Rect-based direction: is the active item center BELOW the over item center?
 *
 * Returns `true` (below → insert after), `false` (above → insert before),
 * or `null` when either rect is unavailable.
 */
function isActiveBelowOver(
  active: DragOverEvent["active"],
  over: NonNullable<DragOverEvent["over"]>,
): boolean | null {
  try {
    const translated = active.rect?.current?.translated
    const overRect = over.rect
    if (translated && overRect) {
      const activeMid = translated.top + translated.height / 2
      const overMid = overRect.top + overRect.height / 2
      return activeMid > overMid
    }
  } catch {
    /* rect unavailable — caller should fallback */
  }
  return null
}

/**
 * Find neighbor-based insertion boundaries when the active item IS the
 * over target (or over is a dragged companion). Uses the active item's
 * position in currentItems to determine where to insert relative to
 * non-dragged items.
 *
 * Returns `{ prevIdx, nextIdx }` for use with `nextIndex()` or
 * `distributeIndexes()`.
 */
function findNeighborBounds(
  currentItems: ViewItem[],
  targetGroupItems: ViewItem[],
  activeId: string,
  overGroup: string,
  getGroupKey: (vi: ViewItem) => string,
  draggedSet?: Set<string>,
): { prevIdx?: number; nextIdx?: number } {
  const allInTarget = currentItems.filter((vi) => getGroupKey(vi) === overGroup)
  const activePos = allInTarget.findIndex((vi) => vi.task.id === activeId)

  // First in group → before first non-dragged item
  if (activePos <= 0) {
    return { nextIdx: targetGroupItems[0]?.sortIndex }
  }
  // Last in group → after last non-dragged item
  if (activePos >= allInTarget.length - 1) {
    return {
      prevIdx: targetGroupItems[targetGroupItems.length - 1]?.sortIndex,
    }
  }

  // Between two items
  if (draggedSet) {
    // Multi-drag: skip all dragged items when finding neighbors
    const nonDragged = allInTarget.filter(
      (vi) => !draggedSet.has(vi.task.id ?? ""),
    )
    const insertPos = nonDragged.findIndex(
      (vi) => allInTarget.indexOf(vi) > activePos,
    )
    if (insertPos > 0) {
      return {
        prevIdx: nonDragged[insertPos - 1]!.sortIndex,
        nextIdx: nonDragged[insertPos]!.sortIndex,
      }
    }
    if (insertPos === 0) {
      return { nextIdx: nonDragged[0]!.sortIndex }
    }
    return { prevIdx: nonDragged[nonDragged.length - 1]?.sortIndex }
  }

  // Single drag: direct neighbor lookup
  return {
    prevIdx: allInTarget[activePos - 1]!.sortIndex,
    nextIdx: allInTarget[activePos + 1]!.sortIndex,
  }
}

// ---------------------------------------------------------------------------
// Views that display grouped task lists
// ---------------------------------------------------------------------------

const GROUPED_VIEWS = new Set(["today", "anyday", "someday"])

// ---------------------------------------------------------------------------
// Visual grouping position helpers
// ---------------------------------------------------------------------------

// computeGroupPositions is imported from ../utils/groupPositions

// ---------------------------------------------------------------------------
// Per-group virtualized task rows
// ---------------------------------------------------------------------------

/**
 * Calculates the stable offset from the scroll container's content start
 * to the given element. Works regardless of nesting depth and positioned
 * parents. Returns 0 if either element is null.
 */
function getScrollMargin(
  el: HTMLElement | null,
  scrollEl: HTMLElement | null,
): number {
  if (!el || !scrollEl) return 0
  const elRect = el.getBoundingClientRect()
  const scrollRect = scrollEl.getBoundingClientRect()
  return elRect.top - scrollRect.top + scrollEl.scrollTop
}

interface GroupRowsProps {
  items: ViewItem[]
  activeTaskId: string | null | undefined
  isEditingTitle: boolean
  onComplete: (id: string) => void
  onUncomplete: (id: string) => void
  onOpen?: (id: string) => void
  onClose?: () => void
  groupFirsts?: boolean[]
  groupLasts?: boolean[]
}

/**
 * VirtualGroupPlainRows — renders TaskItem items using @tanstack/react-virtual.
 *
 * Used for the non-DnD path when a group exceeds the virtualization threshold.
 */
const VirtualGroupPlainRows = memo(function VirtualGroupPlainRows({
  items,
  activeTaskId,
  isEditingTitle,
  onComplete,
  onUncomplete,
  onOpen,
  onClose,
  groupFirsts,
  groupLasts,
}: GroupRowsProps) {
  const { scrollRef } = useMainScroll()
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null)
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setContainerEl(node)
  }, [])

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => {
      const vi = items[index]
      if (vi && vi.task.id === activeTaskId) return 220
      if (vi && vi.task.type === ObjectType.Section) return SECTION_ROW_HEIGHT
      return ROW_HEIGHT
    },
    overscan: VIRTUAL_OVERSCAN,
    scrollMargin: getScrollMargin(containerEl, scrollRef.current),
  })

  return (
    <div ref={containerRef}>
      <div
        className="relative w-full"
        style={{ height: virtualizer.getTotalSize() }}
      >
        {virtualizer.getVirtualItems().map((vRow) => {
          const item = items[vRow.index]
          if (!item) return null
          const taskId = item.task.id ?? `v-${vRow.index}`
          const isActive = item.task.id === activeTaskId
          const isSection = item.task.type === ObjectType.Section
          return (
            <div
              key={taskId}
              style={{
                position: "absolute",
                top: vRow.start - virtualizer.options.scrollMargin,
                left: 0,
                width: "100%",
                height: isActive ? undefined : vRow.size,
                // Sections overflow visually (mt-[30px]) so give them
                // a higher z-index to paint above task rows' z-10 bg-card
                ...(isSection ? { zIndex: 2 } : undefined),
              }}
            >
              <TaskItem
                task={item.task}
                onComplete={onComplete}
                onUncomplete={onUncomplete}
                isActive={isActive}
                startAtData={item.startAtData}
                editTitle={isActive && !!isEditingTitle}
                onOpen={onOpen}
                onClose={onClose}
                isFirstInGroup={groupFirsts?.[vRow.index]}
                isLastInGroup={groupLasts?.[vRow.index]}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
})

/** Check if a view should render tasks grouped by container */
export function viewShowsGrouping(viewId: string): boolean {
  return GROUPED_VIEWS.has(viewId)
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// DnD integration
// ---------------------------------------------------------------------------

export interface GroupedDndProps {
  indexField: "index" | "startAtIndex"
  setOptimistic: (
    updater:
      | ViewItem[]
      | ((prev: ViewItem[] | null) => ViewItem[] | null)
      | null,
  ) => void
}

interface GroupedTaskListProps {
  items: ViewItem[]
  /** When provided, enables drag-to-reorder */
  dndProps?: GroupedDndProps
  /**
   * Max items to show per group initially (e.g. 3 for Anyday).
   * Undefined = show all items (no truncation).
   */
  maxInitialItems?: number
}

/** Sentinel value: group is fully expanded (show all items) */
const SHOW_ALL = Infinity

export const GroupedTaskList = memo(function GroupedTaskList({
  items,
  dndProps,
  maxInitialItems,
}: GroupedTaskListProps) {
  const { completeTask, uncompleteTask } = useTaskActions()
  const { activeTaskId, isEditingTitle, openTask, closeTask } = useActiveTask()

  // ── Per-group paging state ──────────────────────────────────────────
  // Map<containerId, visibleCount>. Only used when maxInitialItems is set.
  const [pagingMap, setPagingMap] = useState<Record<string, number>>({})

  /** Get visible limit for a group. Returns Infinity when paging is off. */
  const getVisibleLimit = useCallback(
    (containerId: string) => {
      if (maxInitialItems == null) return Infinity
      return pagingMap[containerId] ?? maxInitialItems
    },
    [maxInitialItems, pagingMap],
  )

  /** Expand a group to show all items (Things 3 pattern: one click reveals all) */
  const handleShowMore = useCallback((containerId: string) => {
    setPagingMap((prev) => ({
      ...prev,
      [containerId]: SHOW_ALL,
    }))
  }, [])

  /** Collapse a group back to the initial truncated count */
  const handleCollapse = useCallback((containerId: string) => {
    setPagingMap((prev) => {
      const next = { ...prev }
      delete next[containerId]
      return next
    })
  }, [])

  // Subscribe to version to trigger re-group when menu data changes
  const ancestorVersion = useAncestorTagStore((s) => s.version)

  // ── Local drag state (live items during cross-group drag) ─────────────
  // null = use prop items; non-null = overridden items during drag
  const [dragItems, setDragItems] = useState<ViewItem[] | null>(null)

  // Ref to access latest dragItems without stale closures in event handlers
  const dragItemsRef = useRef<ViewItem[] | null>(null)
  dragItemsRef.current = dragItems

  // Snapshot of groups before drag started — used to preserve empty groups
  // so their headers + DroppableGroup wrappers stay mounted during drag.
  const preDragGroupsRef = useRef<TaskGroup[]>([])

  // The effective items: dragItems during drag, prop items otherwise
  const effectiveItems = dragItems ?? items

  const groups = useMemo(() => {
    const state = useAncestorTagStore.getState()
    const computed = groupItems(
      effectiveItems,
      state.containerSortIndex,
      state.containerTitles,
      state.containerTypes,
      state.containerProgress,
    )

    if (dragItems === null) {
      // Not dragging — snapshot groups for future drag preservation
      preDragGroupsRef.current = computed
      return computed
    }

    // During drag: preserve empty groups so their DroppableGroup + header
    // stay mounted and the user can drag back to a now-empty container.
    const existing = new Set(computed.map((g) => g.containerId))
    let merged = false
    for (const pg of preDragGroupsRef.current) {
      if (!existing.has(pg.containerId)) {
        computed.push({
          containerId: pg.containerId,
          title: pg.title,
          type: pg.type,
          progress: pg.progress,
          sortIndex: pg.sortIndex,
          items: [],
        })
        merged = true
      }
    }
    if (merged) computed.sort((a, b) => a.sortIndex - b.sortIndex)

    return computed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveItems, ancestorVersion, dragItems])

  // Ref to access current groups in DragOver callback without stale closures
  const groupsRef = useRef(groups)
  groupsRef.current = groups

  // Flat display-ordered list — needed by DndListProvider for DragOverlay lookup
  const displayItems = useMemo(() => groups.flatMap((g) => g.items), [groups])

  // ── Flat list of actually-rendered items (respects "Show more" paging) ──
  // Used for visibleTaskIds so Shift+Arrow and range-select only reach
  // tasks the user can actually see on screen.
  // Between non-root groups we insert a synthetic separator so that
  // isFirstSelected / isLastSelected breaks at container-group boundaries.
  const GROUP_SEPARATOR_PREFIX = "__group_sep__"
  const renderedIds = useMemo(() => {
    const ids: string[] = []
    for (const g of groups) {
      const limit = getVisibleLimit(g.containerId)
      const visible = limit < g.items.length ? g.items.slice(0, limit) : g.items
      // Insert a separator before non-root groups so selection radius breaks
      if (g.containerId !== ROOT_GROUP_ID && ids.length > 0) {
        ids.push(`${GROUP_SEPARATOR_PREFIX}${g.containerId}`)
      }
      for (const vi of visible) {
        if (
          vi.task.type === ObjectType.Task ||
          vi.task.type === ObjectType.Section
        ) {
          const id = vi.task.id
          if (id) ids.push(id)
        }
      }
    }
    return ids
  }, [groups, getVisibleLimit])

  // ── Push visibleTaskIds in GROUPED display order ────────────────────
  // ViewShell sets visibleTaskIds from query order, but grouped mode displays
  // items in a different order (by container sidebar sort). Override here so
  // Shift+Click range selection matches the visual order users see.
  // Only tasks actually rendered on screen (not behind "Show more") are included.
  // Synthetic group separators are included so isFirstSelected/isLastSelected
  // breaks at container-group headers (they won't be in selectedTaskIds).
  const setVisibleTaskIds = useViewContext((s) => s.setVisibleTaskIds)
  const setVisibleSectionIds = useViewContext((s) => s.setVisibleSectionIds)
  const setGroupedAllTaskIds = useViewContext((s) => s.setGroupedAllTaskIds)
  useEffect(() => {
    setVisibleTaskIds(renderedIds)

    const sectionIds = new Set<string>()
    // Build full group mapping (ALL tasks per group, including hidden behind "Show more")
    const allGroups: string[][] = []
    for (const g of groups) {
      const limit = getVisibleLimit(g.containerId)
      const visible = limit < g.items.length ? g.items.slice(0, limit) : g.items
      for (const vi of visible) {
        if (vi.task.type === ObjectType.Section && vi.task.id) {
          sectionIds.add(vi.task.id)
        }
      }
      // All task IDs in this group (not just visible)
      const groupAllIds: string[] = []
      for (const vi of g.items) {
        if (
          (vi.task.type === ObjectType.Task ||
            vi.task.type === ObjectType.Section) &&
          vi.task.id
        ) {
          groupAllIds.push(vi.task.id)
        }
      }
      allGroups.push(groupAllIds)
    }
    setVisibleSectionIds(sectionIds)
    setGroupedAllTaskIds(allGroups)
  }, [
    renderedIds,
    groups,
    getVisibleLimit,
    setVisibleTaskIds,
    setVisibleSectionIds,
    setGroupedAllTaskIds,
  ])

  // ── Group key helpers ─────────────────────────────────────────────────
  const getGroupKey = useCallback((vi: ViewItem) => {
    const segments = vi.task.parentPath.split("/")
    return segments.length === 2 && !segments[1]!.startsWith("#")
      ? segments[1]!
      : ROOT_GROUP_ID
  }, [])

  const groupKeyToParentPath = useCallback((key: string) => {
    return key === ROOT_GROUP_ID ? "root/#anyday" : `root/${key}`
  }, [])

  // ── DragOver: cross-container transfer ────────────────────────────────
  // When the active item enters a different group, splice it from source
  // to target group so the target's SortableContext shows the placeholder.
  // Throttled: skip rapid cross-group flips within 80ms (boundary oscillation).
  const lastCrossGroupRef = useRef<{ group: string; time: number } | null>(null)
  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event
      if (!over) return

      const currentItems = dragItemsRef.current ?? items
      const activeItem = currentItems.find((vi) => vi.task.id === active.id)
      if (!activeItem) return

      const activeGroup = getGroupKey(activeItem)

      // Resolve over target — could be a task item, a header, or a DroppableGroup
      const overId = over.id as string
      const isOverHeader = overId.startsWith(HEADER_ID_PREFIX)
      const overItem = isOverHeader
        ? undefined
        : currentItems.find((vi) => vi.task.id === overId)

      let overGroup: string
      let escaped = false

      if (overItem) {
        overGroup = getGroupKey(overItem)
      } else if (isOverHeader) {
        // Header boundary: resolve using pointer position relative to header.
        // When pointer is above header center → effective group is the one above.
        // This prevents own-header barrier AND oscillation after escape.
        const resolved = resolveHeaderOverGroup(
          overId.slice(HEADER_ID_PREFIX.length),
          getTranslatedMidY(active),
          over.rect,
          groupsRef.current,
        )
        overGroup = resolved.groupId
        escaped = resolved.escaped
      } else {
        // DroppableGroup container
        overGroup = overId
      }

      // Same group → SortableContext handles intra-group sort natively
      if (activeGroup === overGroup) return

      // §21: Block cross-group moves when a filter is active — the user
      // can't see all items, so cross-container transfer is unsafe.
      if (
        useTagFilterStore.getState().isFiltering() ||
        useViewFilterStore.getState().isFiltering()
      )
        return

      // Block containers from cross-group moves (they must stay in root)
      if (
        activeItem.task.type === ObjectType.Project ||
        activeItem.task.type === ObjectType.List
      )
        return

      // Throttle: skip if we just transferred to the same group <80ms ago
      // (prevents rapid oscillation near group boundaries)
      const now = Date.now()
      const last = lastCrossGroupRef.current
      if (last && last.group === overGroup && now - last.time < 80) return
      lastCrossGroupRef.current = { group: overGroup, time: now }

      // Cross-group: move active item into the target group
      const newParentPath = groupKeyToParentPath(overGroup)
      const without = currentItems.filter((vi) => vi.task.id !== active.id)
      const movedItem: ViewItem = {
        ...activeItem,
        task: { ...activeItem.task, parentPath: newParentPath },
      }

      // Find insertion index — near the over item within the target group
      if (overItem) {
        const overIdx = without.findIndex((vi) => vi.task.id === over.id)
        if (overIdx !== -1) {
          without.splice(overIdx, 0, movedItem)
        } else {
          without.push(movedItem)
        }
      } else {
        // Header, container, or header-escape — insert within target group
        const targetGroupItems = without.filter(
          (vi) => getGroupKey(vi) === overGroup,
        )
        if (targetGroupItems.length > 0) {
          if (escaped) {
            // Escaped upward through own header → bottom of group above
            const last = targetGroupItems[targetGroupItems.length - 1]!
            without.splice(without.indexOf(last) + 1, 0, movedItem)
          } else {
            // Normal header/container → top of target group
            const first = targetGroupItems[0]!
            without.splice(without.indexOf(first), 0, movedItem)
          }
        } else {
          without.push(movedItem)
        }
      }

      setDragItems(without)
    },
    [items, getGroupKey, groupKeyToParentPath],
  )

  // ── DragEnd: persist final position ───────────────────────────────────
  const handleDragEnd = useCallback(
    async (event: DragEndEvent, draggedIds: string[] | null) => {
      const { active, over } = event
      const currentItems = dragItemsRef.current ?? items

      // Clean up drag state
      setDragItems(null)
      lastCrossGroupRef.current = null

      if (!over || !dndProps) return

      // ── Detect cross-group transfer ─────────────────────────────────
      const originalActiveItem = items.find((vi) => vi.task.id === active.id)
      const originalActiveGroup = originalActiveItem
        ? getGroupKey(originalActiveItem)
        : null

      const activeInCurrent = currentItems.find(
        (vi) => vi.task.id === active.id,
      )
      const currentActiveGroup = activeInCurrent
        ? getGroupKey(activeInCurrent)
        : originalActiveGroup
      const wasCrossGroupTransfer =
        originalActiveGroup != null &&
        currentActiveGroup != null &&
        originalActiveGroup !== currentActiveGroup

      if (active.id === over.id && !wasCrossGroupTransfer) return

      const overId = over.id as string
      const isHeader = overId.startsWith(HEADER_ID_PREFIX)
      const isTaskItem = currentItems.some((vi) => vi.task.id === overId)
      const isOverContainer = !isHeader && !isTaskItem

      // ── Header / container drop ─────────────────────────────────────
      if (isHeader || isOverContainer) {
        // Resolve target group — for headers, pointer position determines
        // which side of the boundary the user is on
        let targetGroup: string
        let escaped = false
        if (isHeader) {
          const resolved = resolveHeaderOverGroup(
            overId.slice(HEADER_ID_PREFIX.length),
            getTranslatedMidY(active),
            over.rect,
            groupsRef.current,
          )
          targetGroup = resolved.groupId
          escaped = resolved.escaped
        } else {
          targetGroup = overId
        }

        const activeItem = currentItems.find((vi) => vi.task.id === active.id)
        if (!activeItem) return

        hapticMedium()

        const activeGroup = originalActiveGroup ?? getGroupKey(activeItem)
        const isCrossGroupMove = activeGroup !== targetGroup

        if (!isCrossGroupMove) {
          // ── Intra-group header/container → move to top ─────────────
          const groupTasks = currentItems.filter(
            (vi) => getGroupKey(vi) === activeGroup && vi.task.id !== active.id,
          )
          const topIdx =
            groupTasks.length > 0 ? groupTasks[0]!.sortIndex - 1 : 0

          const reordered = Array.from(items)
          const srcIdx = reordered.findIndex((vi) => vi.task.id === active.id)
          const [moved] = reordered.splice(srcIdx, 1)
          if (!moved) return
          const firstOfGroup = reordered.findIndex(
            (vi) => getGroupKey(vi) === activeGroup,
          )
          reordered.splice(firstOfGroup >= 0 ? firstOfGroup : 0, 0, {
            ...moved,
            sortIndex: topIdx,
          })
          dndProps.setOptimistic(reordered)

          const { indexField } = dndProps
          try {
            const ghostField = otherIndexField(indexField)
            const ghostIdx = await computeGhostIndex(
              ghostField,
              ghostField === "index" ? activeItem.task.parentPath : undefined,
            )
            await reorderTaskDual(
              active.id as string,
              topIdx,
              indexField,
              ghostIdx,
              ghostField,
            )
          } catch (error) {
            console.error(
              "[GroupedTaskList] Header-drop reorder failed:",
              error,
            )
            dndProps.setOptimistic(null)
          }
        } else {
          // ── Cross-group header/container drop ─────────────────────────

          // Block containers from cross-group moves (they must stay in root)
          if (
            activeItem.task.type === ObjectType.Project ||
            activeItem.task.type === ObjectType.List
          )
            return

          const newParentPath = groupKeyToParentPath(targetGroup)
          const { indexField, setOptimistic } = dndProps

          if (draggedIds && draggedIds.length > 1) {
            // Multi-drag cross-group header/container
            const draggedSet = new Set(draggedIds)
            const targetGroupItems = currentItems.filter(
              (vi) =>
                getGroupKey(vi) === targetGroup &&
                !draggedSet.has(vi.task.id ?? ""),
            )

            // Escaped → bottom of target; normal → top of target
            const newIndexes = escaped
              ? distributeIndexes(
                  targetGroupItems.length > 0
                    ? targetGroupItems[targetGroupItems.length - 1]!.sortIndex
                    : undefined,
                  undefined,
                  draggedIds.length,
                )
              : distributeIndexes(
                  undefined,
                  targetGroupItems.length > 0
                    ? targetGroupItems[0]!.sortIndex
                    : undefined,
                  draggedIds.length,
                )

            const draggedItems = draggedIds
              .map((id) => items.find((vi) => vi.task.id === id))
              .filter(Boolean) as ViewItem[]

            const without = items.filter(
              (vi) => !draggedSet.has(vi.task.id ?? ""),
            )

            // Insert at correct position in target group
            if (escaped && targetGroupItems.length > 0) {
              const last = targetGroupItems[targetGroupItems.length - 1]!
              const lastIdx = without.findIndex(
                (vi) => vi.task.id === last.task.id,
              )
              const insertAt = lastIdx >= 0 ? lastIdx + 1 : without.length
              draggedItems.forEach((item, i) => {
                without.splice(insertAt + i, 0, {
                  ...item,
                  sortIndex: newIndexes[i]!,
                  task: { ...item.task, parentPath: newParentPath },
                })
              })
            } else {
              const firstTargetIdx = without.findIndex(
                (vi) => getGroupKey(vi) === targetGroup,
              )
              const insertAt =
                firstTargetIdx >= 0 ? firstTargetIdx : without.length
              draggedItems.forEach((item, i) => {
                without.splice(insertAt + i, 0, {
                  ...item,
                  sortIndex: newIndexes[i]!,
                  task: { ...item.task, parentPath: newParentPath },
                })
              })
            }
            setOptimistic(without)

            try {
              const ghostField = otherIndexField(indexField)
              const ghostBase = await computeGhostIndex(
                ghostField,
                ghostField === "index" ? newParentPath : undefined,
              )
              const bulkItems: BulkReorderItem[] = draggedIds.map((id, i) => ({
                id,
                primaryIndex: newIndexes[i]!,
                ghostIndex: ghostBase - i,
                newParentPath,
              }))
              await bulkReorderDual(bulkItems, indexField, ghostField)
            } catch (error) {
              console.error(
                "[GroupedTaskList] Header multi-drag cross-group failed:",
                error,
              )
              setOptimistic(null)
            }
          } else {
            // Single drag cross-group on header/container
            const targetGroupItems = currentItems.filter(
              (vi) =>
                getGroupKey(vi) === targetGroup && vi.task.id !== active.id,
            )

            // Escaped → bottom of target; normal → top of target
            const newIdx = escaped
              ? targetGroupItems.length > 0
                ? targetGroupItems[targetGroupItems.length - 1]!.sortIndex + 1
                : 0
              : targetGroupItems.length > 0
                ? targetGroupItems[0]!.sortIndex - 1
                : 0

            const without = items.filter((vi) => vi.task.id !== active.id)
            const movedItem: ViewItem = {
              ...activeItem,
              sortIndex: newIdx,
              task: { ...activeItem.task, parentPath: newParentPath },
            }

            if (escaped && targetGroupItems.length > 0) {
              const last = targetGroupItems[targetGroupItems.length - 1]!
              const lastIdx = without.findIndex(
                (vi) => vi.task.id === last.task.id,
              )
              without.splice(
                lastIdx >= 0 ? lastIdx + 1 : without.length,
                0,
                movedItem,
              )
            } else {
              const insertIdx = without.findIndex(
                (vi) => getGroupKey(vi) === targetGroup,
              )
              without.splice(
                insertIdx >= 0 ? insertIdx : without.length,
                0,
                movedItem,
              )
            }
            setOptimistic(without)

            try {
              const ghostField = otherIndexField(indexField)
              const ghostIdx = await computeGhostIndex(
                ghostField,
                ghostField === "index" ? newParentPath : undefined,
              )
              await crossGroupReorder(
                active.id as string,
                newParentPath,
                newIdx,
                indexField,
                ghostIdx,
                ghostField,
              )
            } catch (error) {
              console.error(
                "[GroupedTaskList] Cross-group header-drop failed:",
                error,
              )
              setOptimistic(null)
            }
          }
        }
        return
      }

      // ── Task-target resolution ──────────────────────────────────────
      const activeItem = currentItems.find((vi) => vi.task.id === active.id)
      const overItem = currentItems.find((vi) => vi.task.id === over.id)
      if (!activeItem || !overItem) return

      // Use ORIGINAL items to determine the active item's source group.
      const activeGroup = originalActiveItem
        ? getGroupKey(originalActiveItem)
        : getGroupKey(activeItem)
      const overGroup = getGroupKey(overItem)

      const { indexField, setOptimistic } = dndProps
      const task = activeItem.task
      const taskId = task.id
      if (!taskId) return

      hapticMedium()

      const isCrossGroup = activeGroup !== overGroup

      // ── Multi-drag (unified: handles both cross- and intra-group) ─────
      // V1 parity: each selected task is treated individually. Tasks from
      // different containers get newParentPath set per-task, while tasks
      // already in the target group just get reordered.
      if (draggedIds && draggedIds.length > 1) {
        const targetParentPath = groupKeyToParentPath(overGroup)
        const draggedSet = new Set(draggedIds)

        // Get target group items (excluding all dragged)
        const targetGroupItems = currentItems.filter(
          (vi) =>
            getGroupKey(vi) === overGroup && !draggedSet.has(vi.task.id ?? ""),
        )

        // Direction: null = over is dragged item → neighbor-based positioning
        const insertAfterOver = draggedSet.has(over.id as string)
          ? null
          : (isActiveBelowOver(active, over) ?? false)

        const overIdx = targetGroupItems.findIndex(
          (vi) => vi.task.id === over.id,
        )

        // Calculate insertion boundaries (prevIdx, nextIdx)
        let prevIdx: number | undefined
        let nextIdx: number | undefined
        if (targetGroupItems.length === 0) {
          // empty group — leave both undefined
        } else if (insertAfterOver === null || overIdx === -1) {
          // Over is a dragged item or not found → neighbor-based
          const bounds = findNeighborBounds(
            currentItems,
            targetGroupItems,
            active.id as string,
            overGroup,
            getGroupKey,
            draggedSet,
          )
          prevIdx = bounds.prevIdx
          nextIdx = bounds.nextIdx
        } else if (insertAfterOver) {
          // Insert AFTER over
          prevIdx = targetGroupItems[overIdx]!.sortIndex
          nextIdx =
            overIdx < targetGroupItems.length - 1
              ? targetGroupItems[overIdx + 1]!.sortIndex
              : undefined
        } else {
          // Insert BEFORE over
          prevIdx =
            overIdx > 0 ? targetGroupItems[overIdx - 1]!.sortIndex : undefined
          nextIdx = targetGroupItems[overIdx]!.sortIndex
        }

        const newIndexes = distributeIndexes(
          prevIdx,
          nextIdx,
          draggedIds.length,
        )

        const draggedItems = draggedIds
          .map((id) => items.find((vi) => vi.task.id === id))
          .filter(Boolean) as ViewItem[]

        // Optimistic: remove all dragged, insert at target position
        // Per-task: set parentPath to target only if the task comes from
        // a different group (V1 parity — individual source awareness)
        const without = items.filter((vi) => !draggedSet.has(vi.task.id ?? ""))
        const targetInWithout = without.filter(
          (vi) => getGroupKey(vi) === overGroup,
        )
        const insertBeforeItem = targetInWithout.find(
          (vi) => vi.sortIndex > newIndexes[0]!,
        )
        let insertAt: number
        if (insertBeforeItem) {
          insertAt = without.indexOf(insertBeforeItem)
        } else if (targetInWithout.length > 0) {
          const last = targetInWithout[targetInWithout.length - 1]!
          insertAt = without.indexOf(last) + 1
        } else {
          insertAt = without.length
        }

        draggedItems.forEach((item, i) => {
          const itemGroup = getGroupKey(item)
          const needsMove = itemGroup !== overGroup
          without.splice(insertAt + i, 0, {
            ...item,
            sortIndex: newIndexes[i]!,
            // Only update parentPath when the task is from a different group
            ...(needsMove
              ? { task: { ...item.task, parentPath: targetParentPath } }
              : {}),
          })
        })
        setOptimistic(without)

        // Persist via bulkReorderDual with per-task newParentPath
        try {
          const ghostField = otherIndexField(indexField)
          const ghostBase = await computeGhostIndex(
            ghostField,
            ghostField === "index" ? targetParentPath : undefined,
          )
          const bulkItems: BulkReorderItem[] = draggedIds.map((id, i) => {
            const dragItem = draggedItems.find((di) => di.task.id === id)
            const itemGroup = dragItem ? getGroupKey(dragItem) : overGroup
            const needsMove = itemGroup !== overGroup
            return {
              id,
              primaryIndex: newIndexes[i]!,
              ghostIndex: ghostBase - i,
              // Only set newParentPath for tasks that actually change container
              ...(needsMove ? { newParentPath: targetParentPath } : {}),
            }
          })
          await bulkReorderDual(bulkItems, indexField, ghostField)
        } catch (error) {
          console.error("[GroupedTaskList] Multi-drag failed:", error)
          setOptimistic(null)
        }
        return
      }

      if (!isCrossGroup) {
        // ── Intra-group reorder ───────────────────────────────────────
        // Only consider items within this group for index calculation
        const groupItems = currentItems.filter(
          (vi) => getGroupKey(vi) === activeGroup,
        )
        const sourceIdx = groupItems.findIndex((vi) => vi.task.id === active.id)
        const overIdx = groupItems.findIndex((vi) => vi.task.id === over.id)
        if (sourceIdx === -1 || overIdx === -1 || sourceIdx === overIdx) return

        const newIdx = reorderIndex(
          groupItems,
          sourceIdx,
          overIdx,
          (vi) => vi.sortIndex,
        )

        // Optimistic: splice within the full items array
        const fullSource = items.findIndex((vi) => vi.task.id === active.id)
        const fullOver = items.findIndex((vi) => vi.task.id === over.id)
        const reordered = Array.from(items)
        const [moved] = reordered.splice(fullSource, 1)
        if (!moved) return
        reordered.splice(fullOver, 0, { ...moved, sortIndex: newIdx })
        setOptimistic(reordered)

        // Persist with dual-index write
        try {
          const ghostField = otherIndexField(indexField)
          const ghostIdx = await computeGhostIndex(
            ghostField,
            ghostField === "index" ? task.parentPath : undefined,
          )
          await reorderTaskDual(
            taskId,
            newIdx,
            indexField,
            ghostIdx,
            ghostField,
          )
        } catch (error) {
          console.error("[GroupedTaskList] Intra-group reorder failed:", error)
          setOptimistic(null) // rollback
        }
      } else {
        // ── Cross-group move ──────────────────────────────────────────

        // Block containers from cross-group moves (they must stay in root)
        if (task.type === ObjectType.Project || task.type === ObjectType.List)
          return

        const newParentPath = groupKeyToParentPath(overGroup)

        // Items in target group, excluding the active item
        const targetGroupItems = currentItems.filter(
          (vi) => getGroupKey(vi) === overGroup && vi.task.id !== active.id,
        )

        // Direction: null = active IS the over target → neighbor-based positioning
        // (handleDragOver placed active at a specific position, closestCenter
        // returned active itself because it's now the nearest collision node)
        const insertAfterOver =
          active.id === over.id
            ? null
            : (isActiveBelowOver(active, over) ?? false)

        let newIdx: number
        if (targetGroupItems.length === 0) {
          newIdx = 0
        } else if (insertAfterOver === null) {
          // active.id === over.id — use neighbor-based positioning
          const bounds = findNeighborBounds(
            currentItems,
            targetGroupItems,
            active.id as string,
            overGroup,
            getGroupKey,
          )
          newIdx = nextIndex(bounds.prevIdx, bounds.nextIdx)
        } else {
          const overIdx = targetGroupItems.findIndex(
            (vi) => vi.task.id === over.id,
          )

          if (overIdx === -1) {
            // Fallback: append at end
            newIdx =
              targetGroupItems[targetGroupItems.length - 1]!.sortIndex + 1
          } else if (insertAfterOver) {
            // Insert AFTER over
            const prev = targetGroupItems[overIdx]!.sortIndex
            const next =
              overIdx < targetGroupItems.length - 1
                ? targetGroupItems[overIdx + 1]!.sortIndex
                : undefined
            newIdx = nextIndex(prev, next)
          } else {
            // Insert BEFORE over
            const prev =
              overIdx > 0 ? targetGroupItems[overIdx - 1]!.sortIndex : undefined
            const next = targetGroupItems[overIdx]!.sortIndex
            newIdx = nextIndex(prev, next)
          }
        }

        // Optimistic: rebuild items with moved task in new group.
        // Use sortIndex-based positioning for consistency with DB write.
        const without = items.filter((vi) => vi.task.id !== active.id)
        const movedItem: ViewItem = {
          ...activeItem,
          sortIndex: newIdx,
          task: { ...activeItem.task, parentPath: newParentPath },
        }
        // Find correct insertion point in full array based on newIdx
        const targetInWithout = without.filter(
          (vi) => getGroupKey(vi) === overGroup,
        )
        const insertBeforeItem = targetInWithout.find(
          (vi) => vi.sortIndex > newIdx,
        )
        if (insertBeforeItem) {
          const idx = without.indexOf(insertBeforeItem)
          without.splice(idx, 0, movedItem)
        } else if (targetInWithout.length > 0) {
          // After last item in group
          const last = targetInWithout[targetInWithout.length - 1]!
          const idx = without.indexOf(last)
          without.splice(idx + 1, 0, movedItem)
        } else {
          without.push(movedItem)
        }
        setOptimistic(without)

        // Persist cross-group move
        try {
          const ghostField = otherIndexField(indexField)
          const ghostIdx = await computeGhostIndex(
            ghostField,
            ghostField === "index" ? newParentPath : undefined,
          )
          await crossGroupReorder(
            taskId,
            newParentPath,
            newIdx,
            indexField,
            ghostIdx,
            ghostField,
          )
        } catch (error) {
          console.error("[GroupedTaskList] Cross-group move failed:", error)
          setOptimistic(null) // rollback
        }
      }
    },
    [items, dndProps, getGroupKey, groupKeyToParentPath],
  )

  // ── Auto-expand when active task is in a truncated group ────────────
  // If user opens a task detail that's beyond the paging limit, expand that group
  useEffect(() => {
    if (!activeTaskId || maxInitialItems == null) return
    for (const group of groups) {
      const limit = getVisibleLimit(group.containerId)
      const taskIndex = group.items.findIndex(
        (vi) => vi.task.id === activeTaskId,
      )
      if (taskIndex >= 0 && taskIndex >= limit) {
        // Auto-expand to reveal the active task
        setPagingMap((prev) => ({
          ...prev,
          [group.containerId]: SHOW_ALL,
        }))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTaskId])

  // ── Render grouped items ──────────────────────────────────────────────
  const groupsContent = (
    <div className="flex flex-col">
      {groups.map((group, groupIndex) => {
        const visibleLimit = getVisibleLimit(group.containerId)
        const allItems = group.items
        const visibleItems =
          visibleLimit < allItems.length
            ? allItems.slice(0, visibleLimit)
            : allItems
        const hiddenCount = allItems.length - visibleItems.length

        // Compute visual group positions for border-radius
        // The last visible task before "Show more" gets isLast=true (bottom radius)
        const hasHiddenItems = hiddenCount > 0
        const { firsts: groupFirsts, lasts: groupLasts } =
          computeGroupPositions(visibleItems)
        // If there are hidden items behind "Show more", force the last
        // visible task to have bottom-radius (it's the end of the visible block)
        if (hasHiddenItems && groupLasts.length > 0) {
          // Find last non-section item and ensure it's marked as last
          for (let i = groupLasts.length - 1; i >= 0; i--) {
            if (visibleItems[i]?.task.type !== ObjectType.Section) {
              groupLasts[i] = true
              break
            }
          }
        }

        // Virtualize task rows when the group exceeds the threshold,
        // unless an active (expanded) task is in this group — expanded
        // cards need normal document flow for the margin animation.
        const hasActiveInGroup = visibleItems.some(
          (vi) => vi.task.id === activeTaskId,
        )
        // Skip virtualization for root group when DnD is enabled — root
        // tasks need SortableTaskRow for drag-and-drop reordering.
        const isRootWithDnd = group.containerId === ROOT_GROUP_ID && !!dndProps
        const shouldVirtualizeGroup =
          visibleItems.length > VIRTUALIZE_GROUP_THRESHOLD &&
          !hasActiveInGroup &&
          !isRootWithDnd

        const taskItemIds = visibleItems.map((vi) => vi.task.id ?? "no-id")

        // Include header ID in SortableContext so it animates with the list.
        // Skip when virtualizing — most task IDs have no mounted DOM nodes,
        // which breaks verticalListSortingStrategy rect lookups and can
        // cause useSortable to apply stale transforms to the header.
        const sortableIds =
          dndProps &&
          group.containerId !== ROOT_GROUP_ID &&
          !shouldVirtualizeGroup
            ? [headerSortableId(group.containerId), ...taskItemIds]
            : taskItemIds

        const groupStyle =
          group.containerId !== ROOT_GROUP_ID
            ? { marginTop: groupIndex === 0 ? 6 : 30, marginBottom: 10 }
            : groupIndex === 0
              ? { marginTop: 6, marginBottom: 10 }
              : { marginTop: 16, marginBottom: 10 }

        // When virtualizing, use plain ContainerGroupHeader instead of
        // SortableGroupHeader. The SortableGroupHeader's useSortable hook
        // participates in the SortableContext, but with most virtual items
        // unmounted (no DOM nodes), the strategy can produce bad transforms
        // on the header — pushing it behind the first virtualized row.
        const header =
          group.containerId !== ROOT_GROUP_ID ? (
            dndProps && !shouldVirtualizeGroup ? (
              <SortableGroupHeader
                containerId={group.containerId}
                title={group.title}
                type={group.type}
                progress={group.progress}
                taskCount={allItems.length}
              />
            ) : (
              <ContainerGroupHeader
                containerId={group.containerId}
                title={group.title}
                type={group.type}
                progress={group.progress}
                taskCount={allItems.length}
              />
            )
          ) : null

        const isExpanded = pagingMap[group.containerId] === SHOW_ALL
        const showMoreBtn =
          maxInitialItems != null && allItems.length > maxInitialItems ? (
            isExpanded ? (
              <ShowMoreButton
                count={maxInitialItems}
                onClick={() => handleCollapse(group.containerId)}
                variant="collapse"
              />
            ) : hiddenCount > 0 ? (
              <ShowMoreButton
                count={hiddenCount}
                onClick={() => handleShowMore(group.containerId)}
              />
            ) : null
          ) : null

        const taskElements = dndProps ? (
          shouldVirtualizeGroup ? (
            // When virtualizing, skip SortableContext entirely.
            // Most sortable IDs have no mounted DOM node (the virtualizer
            // only renders ~overscan items), which causes
            // verticalListSortingStrategy to produce broken transforms
            // that displace the header and misposition visible rows.
            // DnD within 20+ item groups isn't practical UX anyway;
            // users can collapse first. Cross-group drops still work
            // via the DroppableGroup wrapper.
            <>
              {header}
              <VirtualGroupPlainRows
                items={visibleItems}
                activeTaskId={activeTaskId}
                isEditingTitle={!!isEditingTitle}
                onComplete={completeTask}
                onUncomplete={uncompleteTask}
                onOpen={openTask}
                onClose={closeTask}
                groupFirsts={groupFirsts}
                groupLasts={groupLasts}
              />
              {showMoreBtn}
            </>
          ) : (
            <SortableContext
              items={sortableIds}
              strategy={verticalListSortingStrategy}
            >
              {header}
              {visibleItems.map((item, itemIndex) => {
                const taskId = item.task.id ?? ""
                const isActive = taskId === activeTaskId
                return (
                  <SortableTaskRow
                    key={taskId}
                    item={item}
                    onComplete={completeTask}
                    onUncomplete={uncompleteTask}
                    isActive={isActive}
                    editTitle={isActive && isEditingTitle}
                    onOpen={openTask}
                    onClose={closeTask}
                    isFirstInGroup={groupFirsts[itemIndex]}
                    isLastInGroup={groupLasts[itemIndex]}
                  />
                )
              })}
              {showMoreBtn}
            </SortableContext>
          )
        ) : (
          <>
            {header}
            {shouldVirtualizeGroup ? (
              <VirtualGroupPlainRows
                items={visibleItems}
                activeTaskId={activeTaskId}
                isEditingTitle={!!isEditingTitle}
                onComplete={completeTask}
                onUncomplete={uncompleteTask}
                onOpen={openTask}
                onClose={closeTask}
                groupFirsts={groupFirsts}
                groupLasts={groupLasts}
              />
            ) : (
              visibleItems.map((item, itemIndex) => {
                const taskId = item.task.id ?? ""
                const isActive = taskId === activeTaskId
                return (
                  <TaskItem
                    key={taskId}
                    task={item.task}
                    onComplete={completeTask}
                    onUncomplete={uncompleteTask}
                    isActive={isActive}
                    startAtData={item.startAtData}
                    editTitle={isActive && isEditingTitle}
                    onOpen={openTask}
                    onClose={closeTask}
                    isFirstInGroup={groupFirsts[itemIndex]}
                    isLastInGroup={groupLasts[itemIndex]}
                  />
                )
              })
            )}
            {showMoreBtn}
          </>
        )

        // Wrap in DroppableGroup when DnD enabled, plain div otherwise
        return dndProps ? (
          <DroppableGroup
            key={group.containerId}
            id={group.containerId}
            style={groupStyle}
          >
            {taskElements}
          </DroppableGroup>
        ) : (
          <div key={group.containerId} style={groupStyle}>
            {taskElements}
          </div>
        )
      })}
    </div>
  )

  // ── With DnD: wrap in shared DndListProvider (grouped mode) ───────────
  if (dndProps) {
    return (
      <DndListProvider
        items={displayItems}
        indexField={dndProps.indexField}
        setOptimistic={dndProps.setOptimistic}
        grouped
        onGroupedDragOver={handleDragOver}
        onGroupedDragEnd={handleDragEnd}
      >
        {groupsContent}
      </DndListProvider>
    )
  }

  return groupsContent
})
